import React, { useState, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { urls } from './config.dev';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faChartLine, faCrown, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Topbar from './Topbar';
import MissingDrugs from './MissingDrugs';
import PTmodal from './PTmodal';
import DetailedPerformanceModal from './DetailedPerformanceModal';

// Register components
ChartJS.register(ArcElement, Tooltip, Legend);

function getStartOfMonth() {
  const today = new Date();
  const start = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1));
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Kampala',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(start);
}

function getEndOfMonth() {
  const today = new Date();
  const end = new Date(Date.UTC(today.getFullYear(), today.getMonth() + 1, 0));
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Kampala',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(end);
}

function Performance() {
  const [performanceData, setPerformanceData] = useState([]);
  const [detailedData, setDetailedData] = useState([]);
  const [message, setMessage] = useState('');
  const [startDate, setStartDate] = useState(getStartOfMonth());
  const [endDate, setEndDate] = useState(getEndOfMonth());
  const [selectedSection, setSelectedSection] = useState('Overall');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [dataAvailable, setDataAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingEmployee, setLoadingEmployee] = useState('');
  const [loadingSection, setLoadingSection] = useState(false);
  const navigate = useNavigate();
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [isPTModalOpen, setIsPTModalOpen] = useState(false);
  const [isDetailedPerformanceModalOpen, setIsDetailedPerformanceModalOpen] = useState(false);
  const [selectedEmployee2, setSelectedEmployee2] = useState(null);

  useEffect(() => {
    const token = getTokenFromUrlOrLocalStorage();
    performSecurityCheck(token);
  }, [startDate, endDate, selectedSection]);

  const getTokenFromUrlOrLocalStorage = () => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');
    return tokenFromUrl || localStorage.getItem('token');
  };
  
  const urlToken = getTokenFromUrlOrLocalStorage();

  const performSecurityCheck = async (token) => {
    try {
      const securityResponse = await fetch(urls.security, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (securityResponse.ok) {
        const securityData = await securityResponse.json();
        if (securityData.message === 'Session valid') {
          fetchPerformanceData(token);
        } else if (securityData.error === 'Session expired') {
          navigate(`/dashboard?token=${securityData.clinic_session_token}`);
        } else {
          navigate('/login');
        }
      } else {
        throw new Error('Failed to perform security check');
      }
    } catch (error) {
      console.error('Error performing security check:', error);
      navigate('/login');
    }
  };

  const fetchPerformanceData = async (token) => {
    try {
      setLoading(true);
      setLoadingSection(true);
      const response = await fetch(urls.fetchperformance, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          startDate,
          endDate,
          section: selectedSection,
        }),
      });

      const data = await response.json();

      if (data.error) {
        setMessage(data.error);
        setDataAvailable(false);
      } else {
        data.sort((a, b) => b.percentage - a.percentage);
        setPerformanceData(data);
        setSelectedEmployee(data[0]?.employee_name || '');
        fetchDetailedPerformance(data[0]?.employee_name || '', token);
        setMessage('');
        setDataAvailable(true);
      }
    } catch (error) {
      console.error('Error fetching performance data:', error);
    } finally {
      setLoading(false);
      setLoadingSection(false);
    }
  };

  const fetchDetailedPerformance = async (employeeName, token) => {
    try {
      setLoadingEmployee(employeeName);
      const response = await fetch(urls.detailedperformance, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ employeeName, token }),
      });

      const data = await response.json();

      if (data.error) {
        setMessage(data.error);
      } else {
        const groupedData = data.reduce((acc, item) => {
          const date = item.Date;
          if (!acc[date]) {
            acc[date] = [];
          }
          acc[date].push(item);
          return acc;
        }, {});

        const sortedDates = Object.keys(groupedData).sort((a, b) => new Date(b) - new Date(a));

        const sortedData = sortedDates.map(date => {
          const dayOfWeek = new Date(date).toLocaleDateString('en-US', { weekday: 'long' });
          return {
            date,
            dayOfWeek,
            items: groupedData[date],
          };
        });

        setDetailedData(sortedData);
        setMessage('');
      }
    } catch (error) {
      console.error('Error fetching detailed performance:', error);
    } finally {
      setLoadingEmployee('');
    }
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const handleSectionChange = (section) => {
    setSelectedSection(section);
    const token = getTokenFromUrlOrLocalStorage();
    fetchPerformanceData(token);
  };

  const handleEmployeeClick = (employeeName) => {
    setSelectedEmployee(employeeName);
    const token = getTokenFromUrlOrLocalStorage();
    fetchDetailedPerformance(employeeName, token);
  };


  const handlePrintRanking = async () => {
    try {
      setLoadingPrint(true);
      const token = getTokenFromUrlOrLocalStorage();
      const rankingData = performanceData.map(item => ({
        name: item.employee_name,
        percentage: item.percentage,
      }));

      const response = await fetch(urls.printRanking, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          rankings: rankingData,
          startDate,
          endDate,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }

      const pdfBlob = await response.blob();
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const pdfWindow = window.open(pdfUrl);

      if (!pdfWindow) {
        alert('Please allow popups for this website');
      }
    } catch (error) {
      console.error('Error printing ranking:', error);
    } finally {
      setLoadingPrint(false);
    }
  };

  const handleOpenPTModal = () => {
    setIsPTModalOpen(true);
  };

  const handleClosePTModal = () => {
    setIsPTModalOpen(false);
  };

  const handleOpenDetailedPerformanceModal = (employeeName) => {
    setSelectedEmployee2(employeeName);
    setIsDetailedPerformanceModalOpen(true);
  };

  const handleCloseDetailedPerformanceModal = () => {
    setIsDetailedPerformanceModalOpen(false);
  }

  const sectionButtons = [
    "Overall", "Triage", "Doctors Room", "Dispensary", "Store",
    "DrugAdministration", "Cashier", "Laboratory", "Radiology",
    "Family Planning", "ANC", "Deliveries", "Client Feedbacks"
  ];

  const pieChartData = {
    labels: performanceData.map(item => item.employee_name),
    datasets: [
      {
        data: performanceData.map(item => item.percentage),
        backgroundColor: performanceData.map((_, index) =>
          `hsl(${(index * 360) / performanceData.length}, 70%, 60%)`
        ),
        borderWidth: 1,
        borderColor: '#fff',
      }
    ]
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: '#f5f7fa',
      fontFamily: 'Segoe UI, Roboto, sans-serif',
      overflow: 'hidden'
    }}>
      <Topbar token={urlToken} />
      <MissingDrugs token={urlToken} />

      <div style={{
        display: 'flex',
        flex: 1,
        padding: '20px',
        gap: '20px',
        overflow: 'hidden',
        height: 'calc(100vh - 80px)',// Adjust based on your topbar heigh
        marginTop: '45px'  // Added margin top
      }}>
        {/* Left Container */}
        <div style={{
          flex: '0 0 65%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #eee'
          }}>
            <h1 style={{
              margin: 0,
              color: '#2c3e50',
              fontSize: '24px',
              fontWeight: '600'
            }}>Employee Qualitative and Quantitative Participation</h1>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              margin: '15px 0',
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <label style={{ fontSize: '14px', color: '#555' }}>From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <label style={{ fontSize: '14px', color: '#555' }}>To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '10px'
            }}>
              {sectionButtons.map((section) => (
                <button
                  key={section}
                  onClick={() => handleSectionChange(section)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: selectedSection === section ? '#3498db' : '#ecf0f1',
                    color: selectedSection === section ? 'white' : '#2c3e50',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  {loadingSection && selectedSection === section ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    section
                  )}
                </button>
              ))}
            </div>
          </div>

          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '0 20px 15px 20px',
              borderBottom: '1px solid #eee'
            }}>
              <h2 style={{
                margin: '15px 0 10px 0',
                color: '#2c3e50',
                fontSize: '18px',
                fontWeight: '600'
              }}>Detailed Participation Summary</h2>

              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '15px'
              }}>
                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', width: '100%', padding: '20px' }}>
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                  </div>
                ) : (
                  performanceData.map((item, index) => (
                    <button
                      key={item.employee_name}
                      onClick={() => handleEmployeeClick(item.employee_name)}
                      style={{
                        padding: '8px 15px',
                        backgroundColor: selectedEmployee === item.employee_name ? '#3498db' : '#ecf0f1',
                        color: selectedEmployee === item.employee_name ? 'white' : '#2c3e50',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      {loadingEmployee === item.employee_name ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <>
                          {index === 0 && <FontAwesomeIcon icon={faCrown} style={{ color: '#f1c40f' }} />}
                          {item.employee_name}
                        </>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>

            <div style={{
              flex: 1,
              padding: '20px',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#bdc3c7',
                borderRadius: '3px'
              }
            }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                </div>
              ) : (
                detailedData.map((group) => (
                  <div key={group.date} style={{ marginBottom: '15px' }}>
                    <h3 style={{
                      margin: '0 0 8px 0',
                      color: '#3498db',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}>
                      {`${group.dayOfWeek}, ${group.date}`}
                    </h3>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px'
                    }}>
                      {group.items.map((item, index) => (
                        <div key={index} style={{
                          padding: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '4px',
                        
                          fontSize: '14px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                          {item.time ? `At ${item.time} ` : ''}
                          {item.Sentence}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Container */}
        <div style={{
          flex: '0 0 35%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '20px',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#bdc3c7',
                borderRadius: '3px'
              }
            }}>
              {dataAvailable ? (
                <>
                  <div style={{
                    height: '300px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <Doughnut
                      data={pieChartData}
                      options={{
                        plugins: {
                          legend: {
                            position: 'right',
                            labels: {
                              boxWidth: 12,
                              padding: 15,
                              font: {
                                size: 12
                              }
                            }
                          }
                        },
                        maintainAspectRatio: false
                      }}
                    />
                  </div>

                  <h2 style={{
                    margin: '10px 0 20px 0',
                    color: '#2c3e50',
                    fontSize: '18px',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {selectedSection} Performance
                  </h2>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    {performanceData.map((item) => (
                      <div
                        key={item.employee_name}
                        onClick={() => handleOpenDetailedPerformanceModal(item.employee_name)}
                        style={{
                          padding: '12px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '6px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          ':hover': {
                            backgroundColor: '#e9ecef'
                          }
                        }}
                      >
                        <span style={{
                          fontWeight: '500',
                          color: '#2c3e50'
                        }}>
                          {item.employee_name}
                        </span>
                        <span style={{
                          fontWeight: '600',
                          color: item.percentage > 75 ? '#27ae60' :
                            item.percentage > 50 ? '#f39c12' : '#e74c3c'
                        }}>
                          {item.percentage}%
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  color: '#e74c3c'
                }}>
                  {message}
                </div>
              )}
            </div>
          </div>

          <div style={{
            padding: '15px 20px',
            borderTop: '1px solid #eee',
            display: 'flex',
            gap: '10px'
          }}>
            <button
              onClick={handlePrintRanking}
              disabled={loadingPrint}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: loadingPrint ? '#95a5a6' : '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loadingPrint ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FontAwesomeIcon icon={loadingPrint ? faSpinner : faPrint} spin={loadingPrint} />
              Print Ranking
            </button>

            <button
              onClick={handleOpenPTModal}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: '#2ecc71',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FontAwesomeIcon icon={faChartLine} />
              Trends
            </button>
          </div>
        </div>
      </div>

      {isPTModalOpen && (
        <PTmodal isOpen={isPTModalOpen} onClose={handleClosePTModal} token={urlToken} />
      )}

      {isDetailedPerformanceModalOpen && (
        <DetailedPerformanceModal
          employeeName={selectedEmployee2}
          onClose={handleCloseDetailedPerformanceModal}
          startDate={startDate}
          endDate={endDate}
          token={urlToken}
        />
      )}
    </div>
  );
}

export default Performance;